// Thin compatibility layer over react-router-dom matching the design
// prototype's router surface (useRoute, navigate, Link). Ported screens keep
// their shape; the app gets real history-based routing.
import { useEffect } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  type NavigateFunction,
} from 'react-router-dom';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

/** Current pathname, e.g. '/features/editor'. */
export function useRoute(): string {
  return useLocation().pathname;
}

let navigator: NavigateFunction | null = null;

/** Mount once inside the router so the bare navigate() works in handlers. */
export function NavigateBridge() {
  const n = useNavigate();
  useEffect(() => {
    navigator = n;
    return () => {
      navigator = null;
    };
  }, [n]);
  return null;
}

/** Imperative navigation outside hook contexts (event handlers, timers). */
export function navigate(to: string, options?: { state?: unknown; replace?: boolean }) {
  if (navigator) navigator(to, options);
  else window.location.assign(to);
}

export interface LinkProps {
  to: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  [key: string]: unknown;
}

export function Link({ to, className, style, children, onClick, ...rest }: LinkProps) {
  return (
    <RouterLink to={to} className={className} style={style} onClick={onClick} {...rest}>
      {children}
    </RouterLink>
  );
}
